import { Badge, Flex, Text } from "@radix-ui/themes";

import {
  BooleanControlPresentation,
  type ControlProps,
  DraftControlPresentation,
  SelectControlPresentation,
} from "./LayoutSettingsBasicControls.pres";
import { BreakpointsControlPresentation } from "./LayoutSettingsBreakpointsControl.pres";
import { HandlesControlPresentation } from "./LayoutSettingsHandlesControl.pres";
import type {
  LayoutSettingChange,
  LayoutSettingFieldView,
  LayoutSettingStatus,
} from "./layoutSettings.types";

const controlIdFor = (panelId: string, fieldId: string) =>
  `${panelId}-field-${fieldId}`;
const descIdFor = (panelId: string, fieldId: string) =>
  `${panelId}-desc-${fieldId}`;
const errorIdFor = (panelId: string, fieldId: string) =>
  `${panelId}-error-${fieldId}`;

const STATUS_LABELS: Record<LayoutSettingStatus, string> = {
  live: "Live",
  remount: "Remount",
  unavailable: "Unavailable",
};

const STATUS_COLORS: Record<LayoutSettingStatus, "green" | "orange" | "gray"> =
  {
    live: "green",
    remount: "orange",
    unavailable: "gray",
  };

interface StatusBadgeProps {
  status: LayoutSettingStatus;
}

const StatusBadge = ({ status }: StatusBadgeProps) => (
  <Badge size="1" variant="soft" color={STATUS_COLORS[status]}>
    {STATUS_LABELS[status]}
  </Badge>
);

interface LayoutSettingsFieldRowPresentationProps {
  panelId: string;
  field: LayoutSettingFieldView;
  onFieldChange: (fieldId: string, change: LayoutSettingChange) => void;
}

export const LayoutSettingsFieldRowPresentation = ({
  panelId,
  field,
  onFieldChange,
}: LayoutSettingsFieldRowPresentationProps) => {
  const { descriptor } = field;
  const controlId = controlIdFor(panelId, descriptor.id);
  const descId = descIdFor(panelId, descriptor.id);
  const errorId = errorIdFor(panelId, descriptor.id);

  const controlProps: ControlProps = {
    field,
    controlId,
    descId,
    errorId,
    onFieldChange,
  };

  const renderControl = () => {
    const { control } = descriptor;
    switch (control.kind) {
      case "boolean":
        return <BooleanControlPresentation {...controlProps} />;
      case "number":
        return <DraftControlPresentation {...controlProps} numeric />;
      case "text":
        return <DraftControlPresentation {...controlProps} numeric={false} />;
      case "select":
        return (
          <SelectControlPresentation {...controlProps} control={control} />
        );
      case "handles":
        return (
          <HandlesControlPresentation {...controlProps} control={control} />
        );
      case "breakpoints":
        return (
          <BreakpointsControlPresentation {...controlProps} control={control} />
        );
    }
  };

  const isHandles = descriptor.control.kind === "handles";

  return (
    <Flex direction="column" gap="1">
      <Flex align="center" gap="2" wrap="wrap">
        {!isHandles && (
          <Text as="label" size="1" htmlFor={controlId}>
            {descriptor.label}
          </Text>
        )}
        <StatusBadge status={descriptor.status} />
        {descriptor.statusNote && (
          <Text size="1" color="gray">
            {descriptor.statusNote}
          </Text>
        )}
      </Flex>
      {renderControl()}
      <Text as="p" size="1" color="gray" id={descId}>
        {descriptor.description}
      </Text>
      {field.error && (
        <Text as="p" size="1" color="red" id={errorId} role="alert">
          {field.error}
        </Text>
      )}
      {field.disabled && field.disabledReason && (
        <Text as="p" size="1" color="gray">
          {field.disabledReason}
        </Text>
      )}
    </Flex>
  );
};
